/**
 * Map Pin Drop Functionality for Job Location
 */

const google = window.google || null;

class MapPinDrop {
	constructor() {
		this.map = null;
		this.marker = null;
		this.mapContainer = null;
		this.toggleBtn = null;
		this.latitudeField = null;
		this.longitudeField = null;
		this.defaultCenter = {lat: -25.2744, lng: 133.7751}; // Australia center
		this.init();
	}

	init() {
		this.mapContainer = document.getElementById('mapContainer');
		this.toggleBtn = document.getElementById('toggleMapBtn');

		// Try different possible field ID patterns
		this.latitudeField = this.findField(['id_job_location-latitude', 'id_latitude', 'latitude']);
		this.longitudeField = this.findField(['id_job_location-longitude', 'id_longitude', 'longitude']);

		// Store references to address fields
		this.addressFields = {
			address: document.getElementById('id_job_location-address_line_1') || document.getElementById('id_location-address_line_1'),
			city: document.getElementById('id_job_location-city') || document.getElementById('id_location-city'),
			state: document.getElementById('id_job_location-state') || document.getElementById('id_location-state'),
			postcode: document.getElementById('id_job_location-postcode') || document.getElementById('id_location-postcode')
		};

		if (!this.toggleBtn) return; // Not on a page with map functionality

		this.setupEventListeners();
		this.loadGoogleMapsAPI();
	}

	findField(possibleIds) {
		for (const id of possibleIds) {
			const field = document.getElementById(id);
			if (field) return field;
		}
		return null;
	}

	setupEventListeners() {
		this.toggleBtn.addEventListener('click', () => {
			this.toggleMap();
		});

		// Add blur event listeners to address fields
		Object.values(this.addressFields).forEach(field => {
			if (field) {
				field.addEventListener('blur', () => {
					this.updateMapCenterFromAddress();
				});
			}
		});
	}

	loadGoogleMapsAPI() {
		if (window.google && window.google.maps) {
			// Already loaded - don't initialize yet, wait for toggle
			return;
		}

		// For now, create a fallback map interface without Google Maps API
	}

	toggleMap() {
		if (this.mapContainer.style.display === 'none') {
			this.mapContainer.style.display = 'block';
			this.toggleBtn.innerHTML = '<i class="fas fa-eye-slash"></i> Hide Map';
			this.toggleBtn.style.border = '1px solid #BDBDBD';
			this.toggleBtn.style.borderRadius = '0px';

			if (!this.map) {
				if (window.google && window.google.maps) {
					this.initializeMap();
				} else {
					this.initializeFallbackMap();
				}
			}
			// Update map center when opening
			this.updateMapCenterFromAddress();
		} else {
			this.mapContainer.style.display = 'none';
			this.toggleBtn.innerHTML = '<i class="fas fa-map-marker-alt"></i> Set Pin Location';
			this.toggleBtn.style.border = '1px solid #BDBDBD';
			this.toggleBtn.style.borderRadius = '0px';
		}
	}

	initializeFallbackMap() {
		const mapElement = document.getElementById('map');
		if (!mapElement) return;

		// Get address for display
		const address = this.buildAddressFromFields();
		const addressDisplay = address ? `<p style="color: #495057; margin: 10px 0;">Job Address: <strong>${address}</strong></p>` : '';

		// Create a simple coordinate input interface
		mapElement.innerHTML = `
            <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; height: 100%; background: #f8f9fa; border: 2px dashed #dee2e6; border-radius: 4px; padding: 20px;">
                <h5 style="color: #6c757d; margin-bottom: 10px;">Set Location Coordinates</h5>
                ${addressDisplay}
                <div style="display: flex; gap: 10px; margin-bottom: 15px;">
                    <div>
                        <label style="display: block; margin-bottom: 5px; font-weight: 500;">Latitude:</label>
                        <input type="number" id="manual-latitude" step="0.000001" placeholder="-25.274398" style="padding: 8px; border: 1px solid #ced4da; border-radius: 4px; width: 150px;">
                    </div>
                    <div>
                        <label style="display: block; margin-bottom: 5px; font-weight: 500;">Longitude:</label>
                        <input type="number" id="manual-longitude" step="0.000001" placeholder="133.775136" style="padding: 8px; border: 1px solid #ced4da; border-radius: 4px; width: 150px;">
                    </div>
                </div>
                <button type="button" id="set-coordinates" style="padding: 8px 16px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
                    <i class="fas fa-map-marker-alt"></i> Set Location
                </button>
                <small style="color: #6c757d; margin-top: 10px; text-align: center;">
                    ${address ? `1. Open <a href="https://www.google.com/maps/search/${encodeURIComponent(address)}" target="_blank">Google Maps</a> for this address<br>
                    2. Right-click on the exact location<br>
                    3. Copy the coordinates and paste above` : `Use Google Maps or another mapping service to find precise coordinates<br>
                    Right-click on the location and copy coordinates`}
                </small>
            </div>
        `;

		// Set up manual coordinate input
		this.setupManualInput();

		// Try to suggest coordinates if we have an address
		if (address) {
			this.suggestCoordinatesForAddress(address);
		}
	}

	suggestCoordinatesForAddress() {
		// Use a free geocoding service as fallback
		// Note: In production, you'd want to use a proper API key
		// You can implement a call to a geocoding service here if needed
		// For now, we'll just provide the Google Maps link
	}

	setupManualInput() {
		const setBtn = document.getElementById('set-coordinates');
		const latInput = document.getElementById('manual-latitude');
		const lngInput = document.getElementById('manual-longitude');

		if (!setBtn || !latInput || !lngInput) return;

		// Populate with existing values if available
		if (this.latitudeField && this.latitudeField.value) {
			latInput.value = this.latitudeField.value;
		}
		if (this.longitudeField && this.longitudeField.value) {
			lngInput.value = this.longitudeField.value;
		}

		setBtn.addEventListener('click', () => {
			const lat = parseFloat(latInput.value);
			const lng = parseFloat(lngInput.value);

			if (isNaN(lat) || isNaN(lng)) {
				alert('Please enter valid latitude and longitude values');
				return;
			}

			// Update hidden form fields if they exist
			if (this.latitudeField) {
				this.latitudeField.value = lat.toFixed(8);
			}
			if (this.longitudeField) {
				this.longitudeField.value = lng.toFixed(8);
			}

			// Show feedback
			this.showManualCoordinatesFeedback(lat, lng);
		});
	}

	showManualCoordinatesFeedback(lat, lng) {
		const feedbackElement = this.mapContainer.querySelector('.form-text');
		if (feedbackElement) {
			feedbackElement.innerHTML = `
                <i class="fas fa-check-circle text-success"></i>
                Location coordinates set: ${lat.toFixed(6)}, ${lng.toFixed(6)}
                <a href="#" class="clear-coordinates-link" style="margin-left: 10px; color: #dc3545;">Clear</a>
            `;

			// Add event listener to clear link
			const clearLink = feedbackElement.querySelector('.clear-coordinates-link');
			if (clearLink) {
				clearLink.addEventListener('click', (e) => {
					e.preventDefault();
					this.clearCoordinates();
				});
			}
		}
	}

	initializeMap() {
		const mapElement = document.getElementById('map');
		if (!mapElement || !window.google) return;

		const center = this.getInitialCenter();

		this.map = new google.maps.Map(mapElement, {
			zoom: 16,
			center: center,
			mapTypeId: google.maps.MapTypeId.HYBRID // Better for rural areas
		});

		// Add existing marker if coordinates exist
		if (this.latitudeField && this.longitudeField && this.latitudeField.value && this.longitudeField.value) {
			const existingPosition = {
				lat: parseFloat(this.latitudeField.value),
				lng: parseFloat(this.longitudeField.value)
			};
			this.addMarker(existingPosition);
			this.map.setCenter(existingPosition);
			this.map.setZoom(17);
		}

		// Add click listener
		this.map.addListener('click', (event) => {
			this.onMapClick(event.latLng);
		});
	}

	getInitialCenter() {
		// If we have existing coordinates, use them
		if (this.latitudeField && this.longitudeField && this.latitudeField.value && this.longitudeField.value) {
			return {
				lat: parseFloat(this.latitudeField.value),
				lng: parseFloat(this.longitudeField.value)
			};
		}

		// Try to get center from address fields
		const address = this.buildAddressFromFields();
		if (address) {
			// Geocode will be called after map initialization
			setTimeout(() => {
				this.geocodeAddress(address);
			}, 100);
		}

		// Return default center for Australia
		return this.defaultCenter;
	}

	buildAddressFromFields() {
		// Try different possible field IDs for address fields
		const addressField = document.getElementById('id_location-address_line_1') ||
			document.getElementById('id_job_location-address_line_1');
		const cityField = document.getElementById('id_location-city') ||
			document.getElementById('id_job_location-city');
		const stateField = document.getElementById('id_location-state') ||
			document.getElementById('id_job_location-state');
		const postcodeField = document.getElementById('id_location-postcode') ||
			document.getElementById('id_job_location-postcode');

		const parts = [];
		if (addressField && addressField.value) parts.push(addressField.value);
		if (cityField && cityField.value) parts.push(cityField.value);
		if (stateField && stateField.value) {
			// Get the text content of the selected option for state
			const selectedOption = stateField.options ? stateField.options[stateField.selectedIndex] : null;
			if (selectedOption && selectedOption.text && selectedOption.text !== 'Please Select') {
				parts.push(selectedOption.text);
			}
		}
		if (postcodeField && postcodeField.value) parts.push(postcodeField.value);

		return parts.length > 0 ? parts.join(', ') + ', Australia' : null;
	}

	geocodeAddress(address) {
		if (!window.google || !this.map) return;

		const geocoder = new google.maps.Geocoder();
		geocoder.geocode({address: address}, (results, status) => {
			if (status === 'OK' && results[0]) {
				const location = results[0].geometry.location;
				this.map.setCenter(location);
				this.map.setZoom(17);
			}
		});
	}

	onMapClick(latLng) {
		// Remove existing marker
		if (this.marker) {
			this.marker.setMap(null);
		}

		// Add new marker
		this.addMarker(latLng);

		// Update form fields if they exist
		if (this.latitudeField) {
			this.latitudeField.value = latLng.lat().toFixed(8);
		}
		if (this.longitudeField) {
			this.longitudeField.value = latLng.lng().toFixed(8);
		}

		// Show feedback
		this.showCoordinatesFeedback(latLng.lat(), latLng.lng());
	}

	addMarker(position) {
		this.marker = new google.maps.Marker({
			position: position,
			map: this.map,
			title: 'Job Location',
			draggable: true
		});

		// Add drag listener for marker
		this.marker.addListener('dragend', (event) => {
			this.onMapClick(event.latLng);
		});
	}

	showCoordinatesFeedback(lat, lng) {
		const feedbackElement = this.mapContainer.querySelector('.form-text');
		if (feedbackElement) {
			feedbackElement.innerHTML = `
                <i class="far fa-check-circle text-success"></i>
                Location set! Coordinates: ${lat.toFixed(6)}, ${lng.toFixed(6)}
                <a href="#" class="clear-coordinates-link" style="margin-left: 10px; color: #dc3545;">Clear</a>
            `;

			// Add event listener to clear link
			const clearLink = feedbackElement.querySelector('.clear-coordinates-link');
			if (clearLink) {
				clearLink.addEventListener('click', (e) => {
					e.preventDefault();
					this.clearCoordinates();
				});
			}
		}
	}

	updateMapCenterFromAddress() {
		// Build current address from fields
		const address = this.buildAddressFromFields();
		if (!address) return;

		// Update the address display in fallback map if it exists
		const addressDisplay = document.querySelector('#map p strong');
		if (addressDisplay) {
			addressDisplay.textContent = address;
		}

		// Update Google Maps link in fallback map
		const mapLink = document.querySelector('#map a[target="_blank"]');
		if (mapLink) {
			mapLink.href = `https://www.google.com/maps/search/${encodeURIComponent(address)}`;
		}

		// If we have a Google Map, geocode the new address
		if (this.map && window.google && window.google.maps) {
			this.geocodeAddress(address);
		}
	}

	clearCoordinates() {
		// Clear the form fields
		if (this.latitudeField) {
			this.latitudeField.value = '';
		}
		if (this.longitudeField) {
			this.longitudeField.value = '';
		}

		// Remove marker from map if it exists
		if (this.marker) {
			this.marker.setMap(null);
			this.marker = null;
		}

		// Clear manual input fields if they exist
		const latInput = document.getElementById('manual-latitude');
		const lngInput = document.getElementById('manual-longitude');
		if (latInput) latInput.value = '';
		if (lngInput) lngInput.value = '';

		// Update feedback text
		const feedbackElement = this.mapContainer.querySelector('.form-text');
		if (feedbackElement) {
			feedbackElement.innerHTML = `
                <i class="fas fa-info-circle"></i>
                Click on the map to set the exact location.
            `;
		}

		// Re-center map to address or default
		if (this.map) {
			const address = this.buildAddressFromFields();
			if (address) {
				this.geocodeAddress(address);
			} else {
				this.map.setCenter(this.defaultCenter);
				this.map.setZoom(16);
			}
		}
	}
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
	new MapPinDrop();
});

// Device-appropriate map link functionality
class DeviceMapLinks {
	static openMap(latitude, longitude, address = '') {
		const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
		const isMac = /Macintosh/.test(navigator.userAgent);

		let mapUrl;

		if (latitude && longitude) {
			if (isIOS || isMac) {
				// Apple Maps for iOS/Mac
				mapUrl = `https://maps.apple.com/?q=${latitude},${longitude}`;
			} else {
				// Google Maps for other devices
				mapUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
			}
		} else if (address) {
			const encodedAddress = encodeURIComponent(address);
			if (isIOS || isMac) {
				mapUrl = `https://maps.apple.com/?q=${encodedAddress}`;
			} else {
				mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
			}
		}

		if (mapUrl) {
			window.open(mapUrl, '_blank');
		}
	}
}

// Make DeviceMapLinks globally available
window.DeviceMapLinks = DeviceMapLinks;